import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { FiCamera, FiPlus, FiEdit2, FiMail, FiPhone, FiMapPin, FiCalendar, FiStar, FiBriefcase } from 'react-icons/fi';
import { RootState } from '../../../../types/redux';
import { ProfileField, ProfileDataType, Label } from '../../../../types/profile';
import { profileService } from '../../../../services';
import Button from '../../../../components/misc/Button';
import ProfileFieldForm from '../../../../components/forms/profile/ProfileFieldForm';
import ProfileDataModal from '../../../../components/modals/ProfileDataModal';
import ProfilePictureModal from '../../../../components/modals/ProfilePictureModal';
import ShareAccountModal from '../../../../components/modals/ShareAccountModal';
import { createEmptyField } from '../../../../utils/formatUtils';
import Avatar from '../../../../components/misc/Avatar';

export default function ProfilePage() {
  const { t } = useTranslation();
  const { alias, profilePicture, profileFields } = useSelector((state: RootState) => state.application);
  const [fields, setFields] = useState<ProfileField[]>([]);
  const [loading, setLoading] = useState(true);

  const [editingFieldIndex, setEditingFieldIndex] = useState<number | null>(null);
  const [editingFieldType, setEditingFieldType] = useState<ProfileDataType | null>(null);
  const [showProfileDataModal, setShowProfileDataModal] = useState(false);
  const [showProfilePictureModal, setShowProfilePictureModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showTypePicker, setShowTypePicker] = useState(false);
  const [fieldError, setFieldError] = useState<string | null>(null);

  const [isEditingName, setIsEditingName] = useState(false);
  const [nameInput, setNameInput] = useState<string>(alias?.replace('+', ' ') || '');
  const nameInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    loadProfileData();
  }, []);

  useEffect(() => {
    if (profileFields) setFields(profileFields);
  }, [profileFields]);

  useEffect(() => {
    if (editingFieldIndex === null) return;
    const base = `[data-edit-index="${editingFieldIndex}"]`;
    const selector =
      editingFieldType === ProfileDataType.PHONENUMBER
        ? `${base} input[type="tel"], ${base} input, ${base} textarea, ${base} select`
        : `${base} input, ${base} textarea, ${base} select`;
    const el = document.querySelector(selector) as HTMLElement | null;
    if (el) el.focus();
  }, [editingFieldIndex, editingFieldType]);

  useEffect(() => {
    if (isEditingName) nameInputRef.current?.focus();
  }, [isEditingName]);

  const typePickerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleOutsideClick: EventListener = (e) => {
      const target = e.target as Node | null;
      if (typePickerRef.current && target && !typePickerRef.current.contains(target)) {
        setShowTypePicker(false);
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowTypePicker(false);
      }
    };

    if (showTypePicker) {
      document.addEventListener('mousedown', handleOutsideClick);
      document.addEventListener('touchstart', handleOutsideClick);
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showTypePicker]);

  const loadProfileData = async () => {
    try {
      const data = await profileService.getUserData();
      setFields(data);
    } catch (error) {
      console.error('Failed to load profile data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStartEditField = (index: number) => {
    setEditingFieldIndex(index);
    setEditingFieldType(fields[index].dataType);
    setFieldError(null);
  };

  const handleStartCreateField = (type: ProfileDataType) => {
    const f = createEmptyField(type);
    const next = [...fields, f];
    setFields(next);
    setEditingFieldIndex(next.length - 1);
    setEditingFieldType(type);
    setShowTypePicker(false);
    setFieldError(null);
  };

  const validateField = (field: ProfileField): { valid: boolean; error?: string } => {
    switch (field.dataType) {
      case ProfileDataType.EMAIL:
        if (!field.email || field.email.trim() === '') {
          return { valid: false, error: t('toast.error.field.emailRequired') };
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(field.email)) {
          return { valid: false, error: t('toast.error.field.emailInvalid') };
        }
        return { valid: true };

      case ProfileDataType.PHONENUMBER:
        if (
          !field.phoneNumber?.prefix ||
          !field.phoneNumber?.suffix ||
          field.phoneNumber.prefix.trim() === '' ||
          field.phoneNumber.suffix.trim() === ''
        ) {
          return { valid: false, error: t('toast.error.field.phoneRequired') };
        }
        return { valid: true };

      case ProfileDataType.ADDRESS:
        if (!field.address) {
          return { valid: false, error: t('toast.error.field.addressRequired') };
        }
        const addr = field.address;
        if (!addr.country || !addr.city || addr.country.trim() === '' || addr.city.trim() === '') {
          return { valid: false, error: t('toast.error.field.addressRequired') };
        }
        if ('streetLine' in addr) {
          if (!addr.streetLine || !addr.zipCode || addr.streetLine.trim() === '' || addr.zipCode.trim() === '') {
            return { valid: false, error: t('toast.error.field.addressRequired') };
          }
        } else if (
          !addr.street ||
          !addr.houseNumber ||
          !addr.postCode ||
          addr.street.trim() === '' ||
          addr.houseNumber.trim() === '' ||
          addr.postCode.trim() === ''
        ) {
          return { valid: false, error: t('toast.error.field.addressRequired') };
        }
        return { valid: true };

      case ProfileDataType.BIRTHDATE:
        if (!field.birthDate || field.birthDate.trim() === '') {
          return { valid: false, error: t('toast.error.field.birthdateRequired') };
        }
        return { valid: true };

      case ProfileDataType.MOMENT:
        if (
          !field.moment?.name ||
          !field.moment?.date ||
          field.moment.name.trim() === '' ||
          field.moment.date.trim() === ''
        ) {
          return { valid: false, error: t('toast.error.field.momentRequired') };
        }
        return { valid: true };

      case ProfileDataType.BUSINESSNAME:
        if (!field.description || field.description.trim() === '') {
          return { valid: false, error: t('toast.error.field.businessNameRequired') };
        }
        return { valid: true };

      default:
        return { valid: true };
    }
  };

  const handleSaveFieldAtIndex = async (index: number) => {
    const field = fields[index];

    const validation = validateField(field);
    if (!validation.valid) {
      setFieldError(validation.error || t('toast.error.field.invalid'));
      return;
    }

    try {
      if (field.id) {
        await profileService.updateField(field);
      } else {
        await profileService.addFields([field]);
      }
      await loadProfileData();
      setEditingFieldIndex(null);
      setFieldError(null);
      toast.success(t('toast.success.addedField'));
    } catch (err) {
      console.error(err);
      toast.error(t('toast.error.loadFailed'));
    }
  };

  const handleCancelEditAtIndex = (index: number) => {
    const original = (profileFields ?? [])[index];
    if (!original) {
      const next = [...fields];
      next.splice(index, 1);
      setFields(next);
    } else {
      const next = [...fields];
      next[index] = original;
      setFields(next);
    }
    setEditingFieldIndex(null);
    setFieldError(null);
  };

  const getFieldsByType = (type: ProfileDataType) => {
    return fields.filter((f) => f.dataType === type);
  };

  const formatPhoneNumber = (field: ProfileField) => {
    if (!field.phoneNumber) return '';
    return `${field.phoneNumber.prefix} ${field.phoneNumber.suffix}`;
  };

  const formatAddress = (field: ProfileField) => {
    if (!field.address) return '';
    const addr = field.address;
    if ('streetLine' in addr) {
      return `${addr.country}, ${addr.city}, ${addr.zipCode}, ${addr.streetLine}`;
    }
    return `${addr.country}, ${addr.city}, ${addr.postCode}, ${addr.street} ${addr.houseNumber}${addr.houseNumberAddition || ''}`;
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const getLabelText = (label?: Label) => {
    switch (label) {
      case Label.WORK:
        return t('page.profile.labels.work');
      case Label.PRIVATE:
        return t('page.profile.labels.private');
      case Label.GENERAL:
        return t('page.profile.labels.general');
      default:
        return t('page.profile.labels.main');
    }
  };

  const renderFieldRow = (label: string, value: string, sublabel?: string, onEdit?: () => void) => (
    <div className="flex flex-col items-start gap-3 py-3 sm:flex-row">
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-primary">{label}</div>
        <div className="mt-0.5 break-words text-sm text-details/80">{value}</div>
      </div>
      {sublabel && (
        <div className="mt-2 flex-shrink-0 sm:mt-0">
          <span className="rounded bg-secondary-50 px-2 py-1 text-xs text-details/70">{sublabel}</span>
        </div>
      )}
      {onEdit && (
        <button
          type="button"
          aria-label={t('page.profile.editField')}
          onClick={onEdit}
          className="hover:bg-secondary-100 mt-2 flex-shrink-0 rounded-lg bg-secondary-50 p-1 sm:mt-0">
          <FiEdit2 className="icon mx-auto h-5 w-5 text-primary" />
        </button>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-details">{t('general.loading')}</div>
      </div>
    );
  }

  const nameField = fields.find((f) => f.dataType === ProfileDataType.BUSINESSNAME);
  const emailFields = getFieldsByType(ProfileDataType.EMAIL);
  const phoneFields = getFieldsByType(ProfileDataType.PHONENUMBER);
  const addressFields = getFieldsByType(ProfileDataType.ADDRESS);
  const birthdayField = fields.find((f) => f.dataType === ProfileDataType.BIRTHDATE);
  const momentFields = getFieldsByType(ProfileDataType.MOMENT);

  return (
    <div className="flex min-h-screen flex-col px-4 py-6 sm:p-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="mb-2 text-2xl font-bold text-primary sm:text-3xl">{t('general.profile')}</h1>
        </div>
      </div>

      {/* Profile Picture Section */}
      <div className="mb-6 flex justify-center">
        <div className="relative" onClick={() => setShowProfilePictureModal(true)}>
          {profilePicture ? (
            <img src={profilePicture} alt="Profile" className="h-20 w-20 rounded-full object-cover sm:h-32 sm:w-32" />
          ) : (
            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-secondary-200 sm:h-32 sm:w-32">
              <Avatar
                alias={alias || ''}
                src={profilePicture}
                className="h-20 w-20 text-2xl sm:h-32 sm:w-32 sm:text-3xl"
              />
            </div>
          )}
          <button
            type="button"
            aria-label={t('page.profile.editPicture')}
            className="absolute -bottom-2 -right-2 flex h-9 w-9 items-center justify-center rounded-full bg-primary">
            <FiCamera className="h-5 w-5 text-secondary" />
          </button>
        </div>
      </div>

      {/* Profile Card */}
      <div className="border-secondary-100 w-full rounded-xl border bg-secondary">
        {/* Header */}
        <div className="flex flex-col items-start justify-between gap-2 rounded-t-xl bg-secondary-200 px-4 py-3 sm:flex-row sm:items-center">
          <h2 className="text-secondary-900 text-lg font-semibold">{t('page.profile.personalInfo')}</h2>
          <div className="relative flex flex-col gap-2 sm:flex-row">
            <div ref={typePickerRef} className="relative">
              <Button
                variant="primary-outline"
                className="w-full px-3 py-1.5 text-sm sm:w-auto"
                onClick={() => setShowTypePicker((s) => !s)}>
                <FiPlus className="h-4 w-4" />
                {t('page.profile.addNewField')}
              </Button>
              {showTypePicker && (
                <div className="absolute left-0 right-0 mt-2 w-full rounded-md border bg-secondary p-2 text-primary shadow sm:left-auto sm:right-0 sm:w-52">
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 rounded p-2 text-left text-primary hover:bg-secondary-50"
                    onClick={() => handleStartCreateField(ProfileDataType.EMAIL)}>
                    <FiMail className="h-4 w-4" />
                    {t('page.profile.emailAddress')}
                  </button>
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 rounded p-2 text-left text-primary hover:bg-secondary-50"
                    onClick={() => handleStartCreateField(ProfileDataType.PHONENUMBER)}>
                    <FiPhone className="h-4 w-4" />
                    {t('page.profile.phoneNumber')}
                  </button>
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 rounded p-2 text-left text-primary hover:bg-secondary-50"
                    onClick={() => handleStartCreateField(ProfileDataType.ADDRESS)}>
                    <FiMapPin className="h-4 w-4" />
                    {t('page.profile.address')}
                  </button>
                  {!fields.find((f) => f.dataType === ProfileDataType.BIRTHDATE) && (
                    <button
                      type="button"
                      className="flex w-full items-center gap-2 rounded p-2 text-left text-primary hover:bg-secondary-50"
                      onClick={() => handleStartCreateField(ProfileDataType.BIRTHDATE)}>
                      <FiCalendar className="h-4 w-4" />
                      {t('page.profile.birthday')}
                    </button>
                  )}
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 rounded p-2 text-left text-primary hover:bg-secondary-50"
                    onClick={() => handleStartCreateField(ProfileDataType.MOMENT)}>
                    <FiStar className="h-4 w-4" />
                    {t('page.profile.specialDates')}
                  </button>
                  {!fields.find((f) => f.dataType === ProfileDataType.BUSINESSNAME) && (
                    <button
                      type="button"
                      className="flex w-full items-center gap-2 rounded p-2 text-left text-primary hover:bg-secondary-50"
                      onClick={() => handleStartCreateField(ProfileDataType.BUSINESSNAME)}>
                      <FiBriefcase className="h-4 w-4" />
                      {t('page.profile.businessName')}
                    </button>
                  )}
                </div>
              )}
            </div>
            <Button
              variant="primary"
              className="w-full px-3 py-1.5 text-sm sm:w-auto"
              onClick={() => setShowShareModal((s) => !s)}>
              {t('page.profile.shareAccount')}
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="divide-secondary-100 divide-y p-4 sm:p-6">
          {/* Name - inline editable */}
          {isEditingName ? (
            <div data-edit-index="name-input" className="flex flex-col items-start gap-3 py-3 sm:flex-row">
              <div className="min-w-0 flex-1">
                <input
                  ref={nameInputRef}
                  className="w-full rounded-lg border border-secondary-200 !bg-secondary-50 px-3 py-2 text-sm"
                  value={nameInput}
                  onChange={(e) => setNameInput(e.target.value)}
                />
              </div>
              <div className="ml-auto mt-2 flex flex-shrink-0 gap-2 sm:mt-0">
                <Button
                  className="underline"
                  onClick={() => {
                    setIsEditingName(false);
                    setNameInput(alias?.replace('+', ' ') || '');
                  }}>
                  {t('general.cancel')}
                </Button>
                <Button
                  variant="primary"
                  onClick={async () => {
                    const parts = nameInput.split(' ').filter(Boolean);
                    if (parts.length < 2) {
                      toast.error(t('toast.error.field.name'));
                      return;
                    }
                    try {
                      await profileService.changeName(`${parts[0].trim()}+${parts.slice(1).join(' ').trim()}`);
                      setIsEditingName(false);
                      await loadProfileData();
                      toast.success(t('toast.success.nameUpdated'));
                    } catch (err) {
                      console.error(err);
                      toast.error(t('toast.error.loadFailed'));
                    }
                  }}>
                  {t('general.save')}
                </Button>
              </div>
            </div>
          ) : (
            renderFieldRow(
              t('page.profile.name'),
              alias?.replace('+', ' ') || t('page.profile.notSet'),
              undefined,
              () => {
                setIsEditingName(true);
              },
            )
          )}

          {/* Business Title */}
          {nameField &&
            (() => {
              const idx = fields.findIndex((f) => f.dataType === ProfileDataType.BUSINESSNAME);
              if (idx === editingFieldIndex) {
                return (
                  <div data-edit-index={idx} key={nameField.id || 'business'} className="py-3">
                    <div className="flex flex-col gap-3">
                      <ProfileFieldForm
                        field={fields[idx]}
                        setField={(f) => {
                          const next = [...fields];
                          next[idx] = f;
                          setFields(next);
                          setFieldError(null);
                        }}
                        showLabelSelector
                        inputBgClassName="!bg-secondary-50"
                      />
                      {fieldError && <div className="text-sm text-error">{fieldError}</div>}
                      <div className="ml-auto mt-2 flex gap-2 sm:mt-0">
                        <Button className="underline" onClick={() => handleCancelEditAtIndex(idx)}>
                          {t('general.cancel')}
                        </Button>
                        <Button variant="primary" onClick={() => handleSaveFieldAtIndex(idx)}>
                          {t('general.save')}
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              }
              return renderFieldRow(t('page.profile.businessName'), nameField.description, undefined, () => {
                if (idx !== -1) handleStartEditField(idx);
              });
            })()}

          {/* Phone Numbers */}
          {phoneFields.map((field) => {
            const idx = fields.findIndex((f) => f.id === field.id);
            if (idx === editingFieldIndex) {
              return (
                <div data-edit-index={idx} key={field.id || idx} className="py-3">
                  <div className="flex flex-col gap-3">
                    <ProfileFieldForm
                      field={fields[idx]}
                      setField={(f) => {
                        const next = [...fields];
                        next[idx] = f;
                        setFields(next);
                        setFieldError(null);
                      }}
                      showLabelSelector
                      inputBgClassName="!bg-secondary-50"
                    />
                    {fieldError && <div className="text-sm text-error">{fieldError}</div>}
                    <div className="ml-auto mt-2 flex gap-2 sm:mt-0">
                      <Button className="underline" onClick={() => handleCancelEditAtIndex(idx)}>
                        {t('general.cancel')}
                      </Button>
                      <Button variant="primary" onClick={() => handleSaveFieldAtIndex(idx)}>
                        {t('general.save')}
                      </Button>
                    </div>
                  </div>
                </div>
              );
            }
            return (
              <div key={field.id || idx}>
                {renderFieldRow(
                  t('page.profile.phoneNumber'),
                  formatPhoneNumber(field),
                  getLabelText(field.label),
                  () => {
                    if (idx !== -1) handleStartEditField(idx);
                  },
                )}
              </div>
            );
          })}

          {/* Birthday */}
          {birthdayField &&
            (() => {
              const idx = fields.findIndex((f) => f.id === birthdayField.id);
              if (idx === editingFieldIndex) {
                return (
                  <div data-edit-index={idx} key={birthdayField.id || 'birth'} className="py-3">
                    <div className="flex flex-col gap-3">
                      <ProfileFieldForm
                        field={fields[idx]}
                        setField={(f) => {
                          const next = [...fields];
                          next[idx] = f;
                          setFields(next);
                          setFieldError(null);
                        }}
                        inputBgClassName="!bg-secondary-50"
                      />
                      {fieldError && <div className="text-sm text-error">{fieldError}</div>}
                      <div className="ml-auto mt-2 flex gap-2 sm:mt-0">
                        <Button className="underline" onClick={() => handleCancelEditAtIndex(idx)}>
                          {t('general.cancel')}
                        </Button>
                        <Button variant="primary" onClick={() => handleSaveFieldAtIndex(idx)}>
                          {t('general.save')}
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              }
              return renderFieldRow(t('page.profile.birthday'), formatDate(birthdayField.birthDate), undefined, () => {
                if (idx !== -1) handleStartEditField(idx);
              });
            })()}

          {/* Email Addresses */}
          {emailFields.map((field) => {
            const idx = fields.findIndex((f) => f.id === field.id);
            if (idx === editingFieldIndex) {
              return (
                <div data-edit-index={idx} key={field.id || idx} className="py-3">
                  <div className="flex flex-col gap-3">
                    <ProfileFieldForm
                      field={fields[idx]}
                      setField={(f) => {
                        const next = [...fields];
                        next[idx] = f;
                        setFields(next);
                        setFieldError(null);
                      }}
                      showLabelSelector
                      inputBgClassName="!bg-secondary-50"
                    />
                    {fieldError && <div className="text-sm text-error">{fieldError}</div>}
                    <div className="ml-auto mt-2 flex gap-2 sm:mt-0">
                      <Button className="underline" onClick={() => handleCancelEditAtIndex(idx)}>
                        {t('general.cancel')}
                      </Button>
                      <Button variant="primary" onClick={() => handleSaveFieldAtIndex(idx)}>
                        {t('general.save')}
                      </Button>
                    </div>
                  </div>
                </div>
              );
            }
            return (
              <div key={field.id || idx}>
                {renderFieldRow(t('page.profile.emailAddress'), field.email || '', getLabelText(field.label), () => {
                  if (idx !== -1) handleStartEditField(idx);
                })}
              </div>
            );
          })}

          {/* Address */}
          {addressFields.map((field) => {
            const idx = fields.findIndex((f) => f.id === field.id);
            if (idx === editingFieldIndex) {
              return (
                <div data-edit-index={idx} key={field.id || idx} className="py-3">
                  <div className="flex flex-col gap-3">
                    <ProfileFieldForm
                      field={fields[idx]}
                      setField={(f) => {
                        const next = [...fields];
                        next[idx] = f;
                        setFields(next);
                        setFieldError(null);
                      }}
                    />
                    {fieldError && <div className="text-sm text-error">{fieldError}</div>}
                    <div className="ml-auto mt-2 flex gap-2 sm:mt-0">
                      <Button className="underline" onClick={() => handleCancelEditAtIndex(idx)}>
                        {t('general.cancel')}
                      </Button>
                      <Button variant="primary" onClick={() => handleSaveFieldAtIndex(idx)}>
                        {t('general.save')}
                      </Button>
                    </div>
                  </div>
                </div>
              );
            }
            return (
              <div key={field.id || idx}>
                {renderFieldRow(t('page.profile.address'), formatAddress(field), getLabelText(field.label), () => {
                  if (idx !== -1) handleStartEditField(idx);
                })}
              </div>
            );
          })}

          {/* Special Dates */}
          {momentFields.map((field) => {
            const idx = fields.findIndex((f) => f.id === field.id);
            if (idx === editingFieldIndex) {
              return (
                <div data-edit-index={idx} key={field.id || idx} className="py-3">
                  <div className="flex flex-col gap-3">
                    <ProfileFieldForm
                      field={fields[idx]}
                      setField={(f) => {
                        const next = [...fields];
                        next[idx] = f;
                        setFields(next);
                        setFieldError(null);
                      }}
                    />
                    {fieldError && <div className="text-sm text-error">{fieldError}</div>}
                    <div className="ml-auto mt-2 flex gap-2 sm:mt-0">
                      <Button className="underline" onClick={() => handleCancelEditAtIndex(idx)}>
                        {t('general.cancel')}
                      </Button>
                      <Button variant="primary" onClick={() => handleSaveFieldAtIndex(idx)}>
                        {t('general.save')}
                      </Button>
                    </div>
                  </div>
                </div>
              );
            }
            return (
              <div key={field.id || idx}>
                {renderFieldRow(
                  field.moment?.name || t('page.profile.specialDates'),
                  formatDate(field.moment?.date),
                  undefined,
                  () => {
                    if (idx !== -1) handleStartEditField(idx);
                  },
                )}
              </div>
            );
          })}
        </div>
      </div>

      <ProfileDataModal open={showProfileDataModal} setOpen={setShowProfileDataModal} />

      <ProfilePictureModal open={showProfilePictureModal} setOpen={setShowProfilePictureModal} />

      <ShareAccountModal open={showShareModal} setOpen={setShowShareModal} />

      {/* Delete Account Section */}
      {/* <div className="mt-6 rounded-xl border border-error/20 p-6">
        <h3 className="text-lg font-semibold text-error mb-2">{t('page.profile.deleteAccount.title')}</h3>
        <p className="text-sm text-details/80 mb-4">
          {t('page.profile.deleteAccount.description')}
        </p>
        <Button variant="danger" className="px-6">
          {t('page.profile.deleteAccount.button')}
        </Button>
      </div> */}
    </div>
  );
}
